// Meta (Facebook) Pixel + attribution helpers.
//
// Everything here is a safe no-op until REACT_APP_META_PIXEL_ID is set (Vercel
// env var, build-time), so the site behaves exactly as before without it.
// Server-side Conversions API events live in api/_utils.js and share event IDs
// with the browser events fired here so Meta de-duplicates them.

import { resolveHook } from '../data/flyerHooks';

const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
const ATTRIBUTION_KEY = 'monarch_attribution';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

let pixelStarted = false;

export const metaPixelEnabled = () => !!PIXEL_ID;

export function initMetaPixel() {
  if (!PIXEL_ID || pixelStarted || typeof window === 'undefined') return;
  pixelStarted = true;
  /* eslint-disable */
  (function (f, b, e, v) {
    if (f.fbq) return;
    const n = (f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    });
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    const t = b.createElement(e);
    t.async = true;
    t.src = v;
    const s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq('init', PIXEL_ID);
}

const shouldSkip = (pathname) => pathname.startsWith('/admin') || pathname.startsWith('/pay');

export function trackPageView(pathname = window.location.pathname) {
  initMetaPixel(); // idempotent — child effects can run before App's
  if (!PIXEL_ID || !window.fbq || shouldSkip(pathname)) return;
  window.fbq('track', 'PageView');
}

export function newEventId() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// params: Meta standard parameters (currency, value, content_ids, ...).
// eventId must match the server-side event ID when the same event is also sent
// through the Conversions API.
export function trackEvent(eventName, params = {}, eventId = newEventId()) {
  initMetaPixel(); // idempotent — React runs child effects before the App's
  if (!PIXEL_ID || !window.fbq) return;
  window.fbq('track', eventName, params, { eventID: eventId });
}

// Remember where the visitor came from. fbclid + UTMs arrive on the landing URL
// (the ad link) but the booking happens several pages later, so stash them.
// A new landing with tracking params replaces the old ones; pages without any
// leave the stored values alone.
export function captureAttribution() {
  try {
    const params = new URLSearchParams(window.location.search);
    const found = {};
    UTM_KEYS.forEach((k) => { if (params.get(k)) found[k] = params.get(k).slice(0, 200); });
    if (params.get('fbclid')) found.fbclid = params.get('fbclid').slice(0, 300);
    // Flyer hook: /flyer?h=<keyword>, or ?hook=<keyword> carried on booking links.
    const rawHook = window.location.pathname === '/flyer' ? params.get('h') : params.get('hook');
    if (rawHook !== null && rawHook !== undefined) found.hook = resolveHook(rawHook);
    else if (window.location.pathname === '/flyer') found.hook = 'unknown';
    if (Object.keys(found).length === 0) return;
    found.landing_path = window.location.pathname;
    found.captured_at = new Date().toISOString();
    localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(found));
  } catch {}
}

export function getAttribution() {
  try {
    return JSON.parse(localStorage.getItem(ATTRIBUTION_KEY) || '{}') || {};
  } catch {
    return {};
  }
}

const readCookie = (name) => {
  try {
    const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return m ? decodeURIComponent(m[1]) : '';
  } catch {
    return '';
  }
};

// Browser identifiers for Conversions API matching. _fbp/_fbc are first-party
// cookies set by the pixel; if the pixel was blocked but we saw an fbclid, build
// _fbc in Meta's documented format (fb.1.<ms timestamp>.<fbclid>).
export function getMetaBrowserIds() {
  const attribution = getAttribution();
  let fbc = readCookie('_fbc');
  if (!fbc && attribution.fbclid) {
    const ts = attribution.captured_at ? Date.parse(attribution.captured_at) : Date.now();
    fbc = `fb.1.${Number.isNaN(ts) ? Date.now() : ts}.${attribution.fbclid}`;
  }
  return { fbp: readCookie('_fbp'), fbc };
}

// Payload the booking wizard sends to the checkout endpoint.
export function getTrackingPayload() {
  const attribution = getAttribution();
  const { fbp, fbc } = getMetaBrowserIds();
  return {
    fbp,
    fbc,
    sourceUrl: window.location.href.split('?')[0],
    utm_source: attribution.utm_source || '',
    utm_medium: attribution.utm_medium || '',
    utm_campaign: attribution.utm_campaign || '',
    utm_content: attribution.utm_content || '',
    hook: attribution.hook && attribution.hook !== 'unknown' ? attribution.hook : '',
  };
}
